import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Wallet, TrendingUp, Banknote, Percent, Loader2 } from "lucide-react";

/**
 * Admin-side dialog to inspect a single driver's wallet — reuses backend
 * `/api/drivers/{id}/wallet` (requires drivers.read permission).
 */
export default function DriverWalletDialog({ driver }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/drivers/${driver.id}/wallet`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wallet load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const totals = data?.totals || { earnings: 0, commission: 0, cash_collected: 0, rides: 0 };
  const txns = data?.transactions || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="bg-transparent border-amber-400/40 text-amber-400 hover:bg-amber-400/10 h-9" data-testid={`driver-wallet-open-${driver.id}`}>
          <Wallet className="w-3.5 h-3.5 mr-1.5" /> Wallet
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-400" /> {driver.name} — Wallet
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Card icon={TrendingUp} label="Net earnings" value={`NZ$${totals.earnings.toFixed(2)}`} tone="emerald" tid="admin-wallet-earnings" />
              <Card icon={Banknote} label="Cash on hand" value={`NZ$${totals.cash_collected.toFixed(2)}`} tone="amber" tid="admin-wallet-cash" />
              <Card icon={Percent} label="Commission" value={`NZ$${totals.commission.toFixed(2)}`} tone="slate" tid="admin-wallet-commission" />
              <Card icon={Wallet} label="Rides" value={totals.rides} tone="violet" tid="admin-wallet-rides" />
            </div>
            <div className="border-t border-white/5 pt-4">
              <div className="text-caption mb-2">Recent transactions</div>
              {txns.length === 0 ? (
                <div className="text-slate-500 text-sm text-center py-8">No transactions yet.</div>
              ) : (
                <div className="space-y-1.5 max-h-96 overflow-auto">
                  {txns.map((t) => (
                    <div key={t.id} className="flex items-center justify-between gap-3 text-xs bg-black/20 border border-white/5 rounded-md px-3 py-2" data-testid={`admin-wallet-txn-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-amber-400 truncate">#{t.booking_ref || "—"}</div>
                        <div className="text-slate-500">{new Date(t.at).toLocaleString()} · {t.payment_kind || t.type}</div>
                      </div>
                      <div className={`font-mono font-bold text-sm ${t.type === "cash_collected" ? "text-amber-300" : "text-emerald-300"}`}>
                        {t.type === "cash_collected" ? `+cash NZ$${(t.amount || 0).toFixed(2)}` : `+NZ$${(t.net || 0).toFixed(2)}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TONES = {
  emerald: "bg-emerald-400/5 border-emerald-400/20 text-emerald-300",
  amber: "bg-amber-400/5 border-amber-400/20 text-amber-300",
  slate: "bg-slate-400/5 border-slate-400/20 text-slate-300",
  violet: "bg-violet-400/5 border-violet-400/20 text-violet-300",
};
const Card = ({ icon: Icon, label, value, tone, tid }) => (
  <div className={`rounded-lg border p-3 ${TONES[tone]}`} data-testid={tid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono opacity-80">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="mt-1 font-display font-bold text-lg">{value}</div>
  </div>
);
